import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  User as FirebaseUser
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { User } from '../types/user';
import { getCurrentUser } from '../services/authService';
import LoadingScreen from '../components/ui/LoadingScreen';
import { apiEndpointManager } from '../services/apiEndpointManager';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<FirebaseUser>;
  login: (email: string, password: string) => Promise<FirebaseUser>;
  loginWithGoogle: () => Promise<FirebaseUser>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from API
  const fetchUserProfile = async (user?: any) => {
    const userToCheck = user || currentUser;
    if (userToCheck) {
      try {
        // Add a small delay to ensure the auth token is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        const profile = await getCurrentUser();
        setUserProfile(profile);
        console.log('User profile fetched successfully');
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // The API failover will be handled automatically by the interceptor
        // If we still get an error here, it means both APIs failed
        // Set a minimal profile to avoid showing 0 coins
        if (userToCheck) {
          console.log('Setting default profile due to API failure');
          setUserProfile({
            id: userToCheck.uid,
            email: userToCheck.email || '',
            nickname: userToCheck.displayName || 'User',
            coinBalance: 1, // Default for new users
            isAdmin: false,
            createdAt: new Date().toISOString(),
            lastActive: new Date().toISOString()
          });
        }
      }
    } else {
      setUserProfile(null);
    }
  };

  // Refresh user profile
  const refreshUserProfile = async () => {
    await fetchUserProfile();
  };

  // Sign up function
  const signup = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    return user;
  };

  // Login function
  const login = (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => userCredential.user);
  };

  // Google login function
  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    const userCredential = await signInWithPopup(auth, provider);
    return userCredential.user;
  };

  // Reset password function
  const resetPassword = (email: string) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Resend verification email function
  const resendVerificationEmail = async () => {
    if (currentUser) {
      await sendEmailVerification(currentUser);
    } else {
      throw new Error('No user is currently logged in');
    }
  };

  // Logout function
  const logout = () => {
    // Clear the cached API endpoint on logout
    apiEndpointManager.clearCache();
    return signOut(auth);
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      
      // Set currentUser for any authenticated user
      if (user) {
        setCurrentUser(user);
        await fetchUserProfile(user);
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingScreen showProgress={true} /> : children}
    </AuthContext.Provider>
  );
};
